/**
 * The LIVE route-guard table — the single source of truth for which session
 * roles `src/routes/index.tsx` actually admits.
 *
 * WHY THIS FILE EXISTS. Before it, the shell nav and the router disagreed
 * about the same screen: nav visibility was filtered by the canonical 6-role
 * matrix (`src/rbac`), while the router still gated on hand-written legacy
 * role arrays (`RoleGuard requiredRoles={["admin","editor"]}`). A `viewer` was
 * shown "List Management" by the matrix and then bounced to /dashboard by the
 * guard — the nav advertised a door the router keeps shut.
 *
 * SESSION VOCABULARY (#590): since NU Core SSO the session role IS one of
 * NU Core's six canonical roles (owner · admin · member · viewer · billing ·
 * tenant_admin) — the legacy Lane-A session roles (admin | customer | viewer
 * | editor) can no longer occur, so the #641 legacy->canonical mapping is moot
 * for the console. Each row below is the ROLE-MATRIX §3 row for that screen
 * (provisional (p) cells included as the matrix states them). The full swap
 * to `src/rbac/RouteGuard` (#652) can now proceed on this vocabulary; until
 * then this table and the matrix must agree row for row.
 *
 * Matching is longest-prefix at path-segment boundaries, the same mechanic
 * `src/rbac/matrix.ts` uses, so `/admin/users` inherits `/admin` and
 * `/lists/analytics` inherits `/lists` without a second table row.
 */

/** One guarded prefix and the raw session roles the live guard admits. */
export interface LiveRouteGuard {
  /** Route prefix as mounted in src/routes/index.tsx (no wildcard suffix). */
  prefix: string;
  /** Raw `user.role` values `RoleGuard` accepts. Order is cosmetic. */
  roles: readonly string[];
}

/**
 * Mirrors — and is the source of — every `RoleGuard` in the route table.
 * Adding a guarded route means adding a row here; there is nowhere else to
 * write the roles, which is the point.
 */
export const LIVE_ROUTE_GUARDS: readonly LiveRouteGuard[] = [
  // ROLE-MATRIX row 4 — List Management (viewer: read-only (p)).
  {
    prefix: "/lists",
    roles: ["owner", "admin", "member", "viewer", "tenant_admin"],
  },
  // Row 5 — Data Enrichment (viewer ❌ (p)).
  {
    prefix: "/enrichment",
    roles: ["owner", "admin", "member", "tenant_admin"],
  },
  // Row 6 — Data Groups (viewer: read-only (p)).
  {
    prefix: "/data-groups",
    roles: ["owner", "admin", "member", "viewer", "tenant_admin"],
  },
  // Row 11 — Suppression: admin tier only. The #637 status mutation is
  // additionally admin-gated server-side (requireAdmin).
  { prefix: "/suppression", roles: ["owner", "admin", "tenant_admin"] },
  // Row 8 — Data Export (member (p)).
  { prefix: "/export", roles: ["owner", "admin", "member", "tenant_admin"] },
  // Row 7 — Connections (member (p)).
  {
    prefix: "/connections",
    roles: ["owner", "admin", "member", "tenant_admin"],
  },
  // Rows 13-16 — shared admin (Tenants/Users/Roles/Audit).
  { prefix: "/admin", roles: ["owner", "admin", "tenant_admin"] },
  // Lane-A customer pages: no canonical role maps to the legacy `customer`
  // session (src/rbac/roles.ts — never map it), so no SSO session reaches
  // them. Kept explicit (not removed) so the route stays GUARDED: an empty
  // roles array would mean "unguarded".
  { prefix: "/customer", roles: ["customer"] },
];

/** Longest prefix first, so /data-groups outranks a future /data. */
const GUARDS_BY_SPECIFICITY: readonly LiveRouteGuard[] = [
  ...LIVE_ROUTE_GUARDS,
].sort((a, b) => b.prefix.length - a.prefix.length);

function normalizePath(path: string): string {
  let p = path.split(/[?#]/)[0];
  if (p.length > 1 && p.endsWith("/")) p = p.slice(0, -1);
  return p;
}

/**
 * The roles the live guard requires for `path`. An EMPTY array means the path
 * carries no `RoleGuard` at all (AuthGuard-only: /dashboard, /search,
 * /settings, /profile, /help, /lead-portal/*) — not "nobody".
 */
export function requiredRolesForPath(path: string): readonly string[] {
  const p = normalizePath(path);
  const hit = GUARDS_BY_SPECIFICITY.find(
    (guard) => p === guard.prefix || p.startsWith(`${guard.prefix}/`),
  );
  return hit ? hit.roles : [];
}

/**
 * Would the LIVE router let this session render `path`?
 *
 * Mirrors `RoleGuard` in src/routes/index.tsx exactly, including its
 * case-sensitive comparison against the raw claim and its `user?.role || ""`
 * coercion — a session whose role string does not match verbatim is refused
 * there, so it must be refused here too. This is authorization-shaped but is
 * NOT the security boundary: every read is enforced server-side.
 */
export function liveGuardAllows(
  role: string | null | undefined,
  path: string,
): boolean {
  const required = requiredRolesForPath(path);
  if (required.length === 0) return true;
  return required.includes(role ?? "");
}
